import { useEffect, useMemo, useState } from "react"
import type {
  FunctionArgs,
  FunctionReference,
  FunctionReturnType,
} from "convex/server"
import { getConvexHttpClient } from "@/lib/convex-http"

type UseConvexQueryOptions = {
  /** Poll for updates without opening a WebSocket (e.g. live stock). */
  refetchInterval?: number
}

type CacheEntry = {
  value: unknown
  timestamp: number
}

const functionName = Symbol.for("functionName")
const queryCache = new Map<string, CacheEntry>()
const pendingQueries = new Map<string, Promise<unknown>>()

const CACHE_TTL_MS = 60_000
const MAX_CACHE_ENTRIES = 100
const MIN_REFETCH_INTERVAL_MS = 30_000

function getQueryName(query: FunctionReference<"query">) {
  return (
    (query as Record<symbol, string | undefined>)[functionName] ??
    String(query)
  )
}

function getQueryKey(query: FunctionReference<"query">, argsKey: string) {
  return `${getQueryName(query)}:${argsKey}`
}

function pruneCache() {
  if (queryCache.size <= MAX_CACHE_ENTRIES) {
    return
  }

  const entriesToRemove = queryCache.size - MAX_CACHE_ENTRIES
  let removed = 0
  for (const key of queryCache.keys()) {
    queryCache.delete(key)
    removed += 1
    if (removed >= entriesToRemove) {
      break
    }
  }
}

async function runCachedQuery<Query extends FunctionReference<"query">>(
  query: Query,
  args: FunctionArgs<Query>,
  key: string,
  forceRefresh = false
): Promise<FunctionReturnType<Query>> {
  const cached = queryCache.get(key)
  const now = Date.now()

  if (!forceRefresh && cached && now - cached.timestamp < CACHE_TTL_MS) {
    return cached.value as FunctionReturnType<Query>
  }

  const pending = pendingQueries.get(key)
  if (pending) {
    return (await pending) as FunctionReturnType<Query>
  }

  const request = getConvexHttpClient()
    .query(query, args)
    .then((value) => {
      queryCache.set(key, { value, timestamp: Date.now() })
      pruneCache()
      return value
    })
    .finally(() => {
      pendingQueries.delete(key)
    })

  pendingQueries.set(key, request)
  return (await request) as FunctionReturnType<Query>
}

export function useConvexQuery<Query extends FunctionReference<"query">>(
  query: Query,
  args: FunctionArgs<Query> | "skip",
  options?: UseConvexQueryOptions
): FunctionReturnType<Query> | undefined {
  const [data, setData] = useState<FunctionReturnType<Query> | undefined>()
  const argsKey = args === "skip" ? "skip" : JSON.stringify(args)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- object args are often inline; rerun only when their serialized value changes
  const stableArgs = useMemo(() => args, [argsKey])
  const refetchInterval = options?.refetchInterval

  useEffect(() => {
    if (stableArgs === "skip") {
      return
    }

    let cancelled = false
    const key = getQueryKey(query, argsKey)

    async function run(forceRefresh = false) {
      try {
        const result = await runCachedQuery(query, stableArgs, key, forceRefresh)
        if (!cancelled) {
          setData(result)
        }
      } catch {
        if (!cancelled) {
          setData(undefined)
        }
      }
    }

    void run()

    if (!refetchInterval) {
      return () => {
        cancelled = true
      }
    }

    const safeRefetchInterval = Math.max(
      refetchInterval,
      MIN_REFETCH_INTERVAL_MS
    )
    const intervalId = window.setInterval(() => {
      void run(true)
    }, safeRefetchInterval)

    return () => {
      cancelled = true
      window.clearInterval(intervalId)
    }
  }, [query, argsKey, stableArgs, refetchInterval])

  return args === "skip" ? undefined : data
}

type QuerySpec = {
  query: FunctionReference<"query">
  args: Record<string, unknown>
}

export function useConvexQueries(
  queries: Record<string, QuerySpec> | "skip"
): Record<string, unknown | Error | undefined> | undefined {
  const [results, setResults] = useState<
    Record<string, unknown | Error | undefined> | undefined
  >()
  const queriesKey = queries === "skip" ? "skip" : JSON.stringify(queries)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- query maps are often inline; rerun only when their serialized value changes
  const stableQueries = useMemo(() => queries, [queriesKey])

  useEffect(() => {
    if (stableQueries === "skip" || Object.keys(stableQueries).length === 0) {
      return
    }

    const entries = Object.entries(stableQueries)
    let cancelled = false

    async function run() {
      const next = await Promise.all(
        entries.map(async ([key, { query, args }]) => {
          try {
            const argsKey = JSON.stringify(args)
            const value = await runCachedQuery(
              query,
              args,
              getQueryKey(query, argsKey)
            )
            return [key, value] as const
          } catch (error) {
            return [
              key,
              error instanceof Error ? error : new Error(String(error)),
            ] as const
          }
        })
      )

      if (!cancelled) {
        setResults(Object.fromEntries(next))
      }
    }

    void run()

    return () => {
      cancelled = true
    }
  }, [stableQueries])

  if (queries === "skip") {
    return undefined
  }

  return Object.keys(queries).length === 0 ? {} : results
}

/** Poll interval for public inventory-style pages. */
export const LIVE_DATA_REFETCH_MS = MIN_REFETCH_INTERVAL_MS
